import { Box, Stack, Text } from '@mantine/core'
import { useRouterState } from '@tanstack/react-router'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import {
  BOGGLE_AMBIENT_DEFAULT_TRANSITION_MS,
  type AmbientPresetId,
} from '~/boggle/ui/ambient/AmbientParticlesConfig'
import { AmbientParticlesLayer } from '~/boggle/ui/ambient/AmbientParticlesLayer'
import { readAmbientDevConfig } from '~/boggle/ui/ambient/ambientParticlesDevStorage'
import { BOGGLE_SHELL_AMBIENT_PAGE_DEFAULT_PRESET } from '~/boggle/ui/chrome/boggleShellAmbientPolicy'
import { BackButton } from '~/boggle/ui/atoms/Buttons'
import { PerformanceOverlay } from '~/boggle/ui/atoms/PerformanceOverlay'
import shell from './RouteShell.module.css'

/** Landing hub (`/`) defers Pixi until the main thread is idle so first paint is lighter. */
const HUB_AMBIENT_IDLE_TIMEOUT_MS = 2000
const HUB_AMBIENT_FADE_MS = 480

/** Shared class names for route chrome (CSS modules). */
export const routeShellUi = {
  ctxMode: shell.ctxMode,
  ctxRace: shell.ctxRace,
  link: shell.link,
  title: shell.title,
} as const

export type BoggleRouteShellAmbientPresetOptions = {
  /** `0` = instant swap (no lerp). Omit for the default blend duration (500ms). */
  transitionMs?: number
}

type AmbientPresetSetter = (
  preset: AmbientPresetId,
  options?: BoggleRouteShellAmbientPresetOptions,
) => void

type AmbientAnimationPausedSetter = (paused: boolean) => void

export const BoggleRouteShellAmbientPresetContext = createContext<AmbientPresetSetter | null>(
  null,
)
const BoggleRouteShellAmbientAnimationPauseContext =
  createContext<AmbientAnimationPausedSetter | null>(null)

/** Low-level ambient driver: returns null outside `RouteShell`. */
export function useBoggleShellBackgroundApply(): AmbientPresetSetter | null {
  return useContext(BoggleRouteShellAmbientPresetContext)
}

/** When the run pause overlay is open, freezes shell ambient particle motion (last frame stays). */
export function useSyncBoggleRouteShellAmbientAnimationPaused(paused: boolean) {
  const setAnimationPaused = useContext(BoggleRouteShellAmbientAnimationPauseContext)

  useEffect(() => {
    if (!setAnimationPaused) {
      return
    }

    setAnimationPaused(paused)
    return () => {
      setAnimationPaused(false)
    }
  }, [paused, setAnimationPaused])
}

/** Call from in-run surfaces when gameplay state should tint the ambient shell mood. */
export function useSyncBoggleRouteShellAmbientPreset(
  preset: AmbientPresetId,
  options?: BoggleRouteShellAmbientPresetOptions,
) {
  const setAmbientPreset = useContext(BoggleRouteShellAmbientPresetContext)

  useEffect(() => {
    if (!setAmbientPreset) {
      return
    }

    setAmbientPreset(preset, options)
    return () => {
      setAmbientPreset('home')
    }
  }, [options?.transitionMs, preset, setAmbientPreset])
}

type BoggleRouteShellProps = {
  children: ReactNode
  className?: string
}

export function RouteShell({ children, className }: BoggleRouteShellProps) {
  const pathname = useRouterState({ select: s => s.location.pathname })
  const isLandingHub = pathname === '/'
  const hubIdleDoneRef = useRef(false)
  const [hubAmbientAllowed, setHubAmbientAllowed] = useState(() => !isLandingHub)
  const [hubAmbientOpaque, setHubAmbientOpaque] = useState(() => !isLandingHub)

  useEffect(() => {
    if (!isLandingHub) {
      hubIdleDoneRef.current = true
      setHubAmbientAllowed(true)
      setHubAmbientOpaque(true)
      return
    }
    if (hubIdleDoneRef.current) {
      setHubAmbientAllowed(true)
      setHubAmbientOpaque(true)
      return
    }
    setHubAmbientAllowed(false)
    setHubAmbientOpaque(false)
    if (typeof window === 'undefined') {
      return
    }
    const run = () => {
      hubIdleDoneRef.current = true
      setHubAmbientAllowed(true)
    }
    let idleHandle: number | undefined
    let timeoutHandle: ReturnType<typeof setTimeout> | undefined
    if ('requestIdleCallback' in window) {
      idleHandle = window.requestIdleCallback(run, { timeout: HUB_AMBIENT_IDLE_TIMEOUT_MS })
    } else {
      timeoutHandle = setTimeout(run, 1)
    }
    return () => {
      if (idleHandle !== undefined) {
        window.cancelIdleCallback(idleHandle)
      }
      if (timeoutHandle !== undefined) {
        clearTimeout(timeoutHandle)
      }
    }
  }, [isLandingHub])

  const [ambientShell, setAmbientShell] = useState<{
    preset: AmbientPresetId
    transitionMs?: number
  }>({ preset: BOGGLE_SHELL_AMBIENT_PAGE_DEFAULT_PRESET })
  const [ambientAnimationPaused, setAmbientAnimationPaused] = useState(false)
  const [ambientDevConfig] = useState(readAmbientDevConfig)

  const setAmbientPreset = useCallback<AmbientPresetSetter>((preset, opts) => {
    const transitionMs =
      opts?.transitionMs !== undefined ? opts.transitionMs : BOGGLE_AMBIENT_DEFAULT_TRANSITION_MS

    setAmbientShell(current => {
      if (current.preset === preset && current.transitionMs === transitionMs) {
        return current
      }

      return { preset, transitionMs }
    })
  }, [])
  const setAmbientAnimationPausedFn = useCallback<AmbientAnimationPausedSetter>(paused => {
    setAmbientAnimationPaused(current => (current === paused ? current : paused))
  }, [])
  const hubAmbientHostStyle = useMemo(
    () => ({
      opacity: hubAmbientOpaque ? 1 : 0,
      transition: `opacity ${HUB_AMBIENT_FADE_MS}ms ease-out`,
    }),
    [hubAmbientOpaque],
  )
  const onAmbientReady = useCallback(() => {
    setHubAmbientOpaque(true)
  }, [])
  const shellClass = [shell.root, className ?? ''].filter(Boolean).join(' ')

  return (
    <BoggleRouteShellAmbientPresetContext.Provider value={setAmbientPreset}>
      <BoggleRouteShellAmbientAnimationPauseContext.Provider
        value={setAmbientAnimationPausedFn}
      >
        <Box component="main" className={shellClass}>
          {hubAmbientAllowed ? (
            <AmbientParticlesLayer
              animationPaused={ambientAnimationPaused}
              config={ambientDevConfig}
              hostStyle={hubAmbientHostStyle}
              onAmbientReady={onAmbientReady}
              preset={ambientShell.preset}
              transitionMs={
                ambientShell.transitionMs ?? BOGGLE_AMBIENT_DEFAULT_TRANSITION_MS
              }
            />
          ) : null}
          <Stack
            className={shell.stackAboveAmbient}
            gap={0}
            h="100%"
            maw={800}
            miw={0}
            mx="auto"
            w="100%"
          >
            <Box flex={1} mih={0} miw={0} style={{ minHeight: 0 }}>
              {children}
            </Box>
          </Stack>
          <PerformanceOverlay />
        </Box>
      </BoggleRouteShellAmbientAnimationPauseContext.Provider>
    </BoggleRouteShellAmbientPresetContext.Provider>
  )
}

/** Optional header context line (e.g. mode label). No primary title row. */
export function RouteTitle({
  contextClassName = routeShellUi.ctxRace,
  contextColor,
  contextLabel,
}: {
  contextClassName?: string
  contextColor?: string
  contextLabel?: string | null
}) {
  if (!contextLabel) {
    return null
  }

  const isRaceShellContext = contextClassName === routeShellUi.ctxRace

  return (
    <Text
      c={
        contextColor ??
        (contextClassName === routeShellUi.ctxMode
          ? 'rgba(242, 232, 217, 0.74)'
          : undefined)
      }
      className={contextClassName}
      data-testid="boggle-shell-context"
      fz={contextClassName === routeShellUi.ctxMode ? 15 : 32}
      left={isRaceShellContext ? 53 : undefined}
      opacity={contextClassName === routeShellUi.ctxMode ? undefined : 0.8}
      pos={isRaceShellContext ? 'relative' : undefined}
      top={isRaceShellContext ? -6 : undefined}
    >
      {contextLabel}
    </Text>
  )
}

export function RouteBackButton({ onClick }: { onClick: () => void }) {
  return (
    <BackButton
      bdrs={8}
      fz={18}
      h={48}
      iconSize={16}
      onClick={onClick}
      px={16}
      variant="subtle"
    />
  )
}
